import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/pages/home/components/Footer';
import FloatingActions from '@/pages/home/components/FloatingActions';

const facility = {
  slug: 'washing-laundry',
  name: 'Clean Wash Laundry Ltd.',
  tagline: 'Industrial Garment Washing & Finishing',
  description: 'Specialized industrial garment washing and laundry facility with enzyme, acid, pigment, and silicone wash capabilities, dry processing, and advanced finishing — giving garments the perfect hand-feel, look, and drape.',
  longDescription: 'Clean Wash Laundry Ltd. is the Beaumonde Tex group\'s specialized garment washing and finishing facility. We handle everything from basic rinse washes to complex multi-step treatments — enzyme washes for soft-hand feel, acid washes for high-contrast denim effects, pigment dyeing and tinting for unique color aesthetics, and silicone finishing for premium softness. Our 40 industrial washing machines, advanced dry processing stations, and state-of-the-art Effluent Treatment Plant with zero-liquid-discharge capability make us one of the most capable and environmentally responsible garment washing facilities in Bangladesh. Monthly capacity of 400,000 pieces across all wash types.',
  heroImage: 'https://readdy.ai/api/search-image?query=Modern%20industrial%20garment%20washing%20facility%20with%20rows%20of%20large%20stainless%20steel%20washing%20machines%2C%20clean%20organized%20workspace%2C%20bright%20LED%20lighting%2C%20professional%20laundry%20equipment%2C%20modern%20textile%20finishing%20photography%2C%20warm%20neutral%20industrial%20aesthetic&width=1800&height=800&seq=washing-hero-individual&orientation=landscape',
  capacityStats: [
    { value: '45,000', label: 'Sq. Ft. Facility', icon: 'ri-building-4-line' },
    { value: '250+', label: 'Skilled Workforce', icon: 'ri-team-line' },
    { value: '40', label: 'Wash Machines', icon: 'ri-drop-line' },
    { value: '400,000', label: 'Monthly Capacity (Pcs)', icon: 'ri-bar-chart-line' },
    { value: '30', label: 'Dryers', icon: 'ri-contrast-drop-line' },
    { value: '12', label: 'Dry Process Stations', icon: 'ri-brush-3-line' },
  ],
  machines: [
    { name: 'Industrial Washers', qty: '40', spec: 'Tonello & Ellis, 100-500 kg capacity', desc: 'Programmable wash cycles for enzyme, acid, pigment, silicone, and specialty treatments.' },
    { name: 'Industrial Dryers', qty: '30', spec: 'Challenge & Pellerin Milnor, 100-300 kg', desc: 'High-efficiency gas and steam dryers with moisture-sensing auto-stop technology.' },
    { name: 'Ozone Wash Systems', qty: '8', spec: 'Jeanologia G2 & e-Flow', desc: 'Low-water ozone washing technology that reduces water usage by up to 80% versus traditional methods.' },
    { name: 'Hydro Extractors', qty: '6', spec: 'Ellis & Braun, 60-200 kg', desc: 'High-speed centrifugal extraction for efficient moisture removal before drying.' },
    { name: 'Dry Process Stations', qty: '12', spec: 'Hand-sanding, whiskering, grinding', desc: 'Manual and automated dry processing for distressed, vintage, and worn-in effects.' },
    { name: 'Effluent Treatment Plant', qty: '1', spec: 'ZLD-capable, 500 m³/day', desc: 'Advanced ETP with reverse osmosis and evaporation for zero liquid discharge compliance.' },
  ],
  gallery: [
    {
      src: 'https://readdy.ai/api/search-image?query=Rows%20of%20industrial%20garment%20washing%20machines%20in%20a%20modern%20clean%20facility%2C%20organized%20workflow%2C%20stainless%20steel%20equipment%2C%20bright%20lighting%2C%20professional%20textile%20laundry%20photography&width=700&height=500&seq=washing-gallery-01&orientation=landscape',
      alt: 'Wash House Floor',
    },
    {
      src: 'https://readdy.ai/api/search-image?query=Industrial%20garment%20dryers%20in%20a%20clean%20organized%20laundry%20facility%2C%20modern%20drying%20equipment%2C%20bright%20workspace%2C%20professional%20textile%20industry%20photography%2C%20warm%20atmosphere&width=700&height=500&seq=washing-gallery-02&orientation=landscape',
      alt: 'Drying Section',
    },
    {
      src: 'https://readdy.ai/api/search-image?query=Jeanologia%20ozone%20washing%20machine%20in%20a%20modern%20textile%20facility%2C%20advanced%20sustainable%20washing%20technology%2C%20clean%20industrial%20environment%2C%20professional%20photography&width=700&height=500&seq=washing-gallery-03&orientation=landscape',
      alt: 'Ozone Wash Technology',
    },
    {
      src: 'https://readdy.ai/api/search-image?query=Dry%20processing%20workstation%20with%20hand%20sanding%20and%20whiskering%20tools%2C%20denim%20garments%20being%20processed%2C%20organized%20clean%20workspace%2C%20professional%20industrial%20photography&width=700&height=500&seq=washing-gallery-04&orientation=landscape',
      alt: 'Dry Processing Station',
    },
    {
      src: 'https://readdy.ai/api/search-image?query=Modern%20effluent%20treatment%20plant%20with%20water%20treatment%20tanks%20and%20equipment%2C%20clean%20industrial%20facility%2C%20environmental%20technology%2C%20professional%20industrial%20photography%2C%20bright%20daylight&width=700&height=500&seq=washing-gallery-05&orientation=landscape',
      alt: 'Effluent Treatment Plant',
    },
    {
      src: 'https://readdy.ai/api/search-image?query=Garment%20finishing%20and%20pressing%20station%20with%20workers%20handling%20washed%20denim%20on%20tables%2C%20organized%20clean%20workspace%2C%20bright%20industrial%20lighting%2C%20professional%20photography&width=700&height=500&seq=washing-gallery-06&orientation=landscape',
      alt: 'Finishing & Pressing',
    },
  ],
  capabilities: [
    { icon: 'ri-test-tube-line', title: 'Enzyme Wash', desc: 'Biological enzyme treatments for soft-hand feel, vintage character, and reduced pilling on cellulose fabrics.' },
    { icon: 'ri-flashlight-line', title: 'Acid Wash', desc: 'Chemical treatments creating high-contrast, marbled effects on denim — from subtle to bold.' },
    { icon: 'ri-paint-line', title: 'Pigment Dye', desc: 'Garment dyeing with pigments for unique color effects, vintage aesthetics, and wash-down character.' },
    { icon: 'ri-contrast-drop-line', title: 'Silicone Wash', desc: 'Premium silicone softener application for ultra-soft hand feel and enhanced drape on finished garments.' },
    { icon: 'ri-checkbox-blank-circle-line', title: 'Stone Wash', desc: 'Pumice stone tumbling for authentic worn-in look, surface abrasion, and soft-hand character on denim.' },
    { icon: 'ri-brush-3-line', title: 'Dry Processing', desc: 'Hand sanding, whiskering, grinding, tagging, and potassium spray for distressed and worn-in effects.' },
    { icon: 'ri-earth-line', title: 'Ozone Wash', desc: 'Sustainable ozone technology reducing water and chemical use by up to 80% while achieving premium finishes.' },
    { icon: 'ri-sun-line', title: 'Tinting', desc: 'Garment tinting and over-dyeing for custom color casts, tonal variations, and fashion-forward aesthetics.' },
  ],
};

export default function WashingLaundryPage() {
  const navigate = useNavigate();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('opacity-100', 'translate-y-0');
            entry.target.classList.remove('opacity-0', 'translate-y-8');
          }
        });
      },
      { threshold: 0.1 },
    );
    document.querySelectorAll('.animate-on-scroll').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Hero */}
      <section className="relative h-[500px] md:h-[600px] flex items-end overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${facility.heroImage})` }}></div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/25"></div>
        <div className="relative z-10 w-full max-w-[1280px] mx-auto px-4 md:px-6 lg:px-8 pb-14 md:pb-20">
          <button
            onClick={() => navigate('/sister-concerns')}
            className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm transition-colors duration-200 mb-5 whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            All Sister Concerns
          </button>
          <span className="inline-block px-3 py-1 rounded-full bg-white/15 text-white text-xs font-semibold font-heading mb-4">
            Washing & Laundry
          </span>
          <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-3 leading-tight">
            {facility.name}
          </h1>
          <p className="text-lg md:text-xl text-white/80 font-heading font-medium mb-4">{facility.tagline}</p>
          <p className="max-w-[650px] text-sm md:text-base text-white/65 leading-relaxed">{facility.description}</p>
        </div>
      </section>

      {/* Capacity Dashboard */}
      <section className="relative -mt-10 z-20">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 lg:px-8">
          <div className="bg-white rounded-lg border border-[#E6EAF0] p-6 md:p-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
            {facility.capacityStats.map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="w-9 h-9 mx-auto flex items-center justify-center rounded-full bg-primary-50 mb-2">
                  <i className={`${stat.icon} text-primary-500 text-base`}></i>
                </div>
                <div className="font-heading text-xl md:text-2xl font-bold text-foreground-900">{stat.value}</div>
                <div className="text-[10px] text-foreground-500 mt-1 leading-tight">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="pt-16 pb-14 md:pt-24 md:pb-20">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-16">
            <div className="lg:w-3/5 animate-on-scroll opacity-0 translate-y-8 transition-all duration-700">
              <span className="inline-block text-xs font-semibold tracking-[0.2em] uppercase text-primary-500 mb-4">About the Facility</span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-6 leading-tight">
                The Perfect Finish. Every Time.
              </h2>
              <p className="text-foreground-600 leading-relaxed text-sm md:text-base mb-5">{facility.longDescription}</p>
            </div>
            <div className="lg:w-2/5 animate-on-scroll opacity-0 translate-y-8 transition-all duration-700 delay-200">
              <div className="bg-background-100 rounded-lg p-6 border border-[#E6EAF0]">
                <h3 className="font-heading text-sm font-semibold text-foreground-800 mb-4 flex items-center gap-2">
                  <i className="ri-settings-3-line text-primary-500"></i>
                  Machine & Equipment
                </h3>
                <div className="space-y-4">
                  {facility.machines.map((m) => (
                    <div key={m.name} className="border-b border-[#E6EAF0] pb-3 last:border-0 last:pb-0">
                      <div className="flex items-start justify-between gap-3 mb-1">
                        <p className="text-sm font-semibold text-foreground-800">{m.name}</p>
                        <span className="text-xs font-bold text-primary-500 whitespace-nowrap">{m.qty} units</span>
                      </div>
                      <p className="text-[11px] text-foreground-500 mb-1">{m.spec}</p>
                      <p className="text-xs text-foreground-400 leading-snug">{m.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Photo Gallery */}
      <section className="py-14 md:py-20 bg-background-100">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 lg:px-8">
          <div className="text-center mb-12 animate-on-scroll opacity-0 translate-y-8 transition-all duration-700">
            <span className="inline-block text-xs font-semibold tracking-[0.2em] uppercase text-primary-500 mb-4">Inside the Facility</span>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">Photo Tour</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {facility.gallery.map((img, i) => (
              <div
                key={img.alt}
                className="rounded-lg overflow-hidden border border-[#E6EAF0] animate-on-scroll opacity-0 translate-y-8 transition-all duration-700 group"
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <img src={img.src} alt={img.alt} className="w-full h-[280px] object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="bg-white px-4 py-3">
                  <p className="text-sm font-medium text-foreground-700">{img.alt}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities Grid */}
      <section className="py-14 md:py-20 bg-white">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 lg:px-8">
          <div className="text-center mb-12 animate-on-scroll opacity-0 translate-y-8 transition-all duration-700">
            <span className="inline-block text-xs font-semibold tracking-[0.2em] uppercase text-primary-500 mb-4">What We Do</span>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">Wash & Finish Capabilities</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {facility.capabilities.map((cap, i) => (
              <div
                key={cap.title}
                className="bg-background-100 rounded-lg p-6 border border-[#E6EAF0] animate-on-scroll opacity-0 translate-y-8 transition-all duration-700 hover:border-primary-200"
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-50 mb-4">
                  <i className={`${cap.icon} text-primary-500 text-lg`}></i>
                </div>
                <h3 className="font-heading text-base font-semibold text-foreground-800 mb-2">{cap.title}</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">{cap.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-16 md:py-24 overflow-hidden bg-primary-500">
        <div className="relative z-10 max-w-[1280px] mx-auto px-4 md:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">Give Your Garments the Perfect Finish</h2>
          <p className="text-white/80 max-w-[500px] mx-auto mb-8 text-sm md:text-base leading-relaxed">
            From enzyme soft washes to premium dry processing — our washing facility delivers the hand-feel and look your customers expect.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => navigate('/#contact')}
              className="inline-flex items-center px-8 py-3.5 bg-white text-primary-600 text-sm font-semibold font-heading rounded-md hover:bg-background-100 transition-colors duration-200 whitespace-nowrap"
            >
              Send an Inquiry
              <i className="ri-arrow-right-line ml-2"></i>
            </button>
            <button
              onClick={() => navigate('/sister-concerns')}
              className="inline-flex items-center px-8 py-3.5 bg-transparent border-2 border-white/30 text-white text-sm font-semibold font-heading rounded-md hover:bg-white/10 transition-colors duration-200 whitespace-nowrap"
            >
              All Sister Concerns
            </button>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingActions />
    </div>
  );
}